import { KeyboardArrowDown } from '@mui/icons-material'
import { Menu, MenuItem, Button } from '@mui/material'
import { type SweepstakeWinnerStatusType } from 'types/api'
import { SweepstakeNotifyWinnersModal } from './SweepstakeNotifyWinnersModal'
import { type SelectedWinnerType } from 'pages/Contests/Sweeptakes/SweepstakesWinnersPage/SweepstakesWinnersPage'
import { usePutSweepstakeWinnerBulkUpdateStatus } from 'hooks/api/Sweepstake/usePutSweepstakeWinnerBulkUpdateStatus'
import { useSnackbar } from 'stores/useSnackbar'
import { useState } from 'react'
import { type QueryKey } from '@tanstack/react-query'
import { getSweepstakeDrawingInfoQueryKey } from 'hooks/api/useGetSweepstakeByIdWithDrawingInfo'

interface MenuButtonProps {
  selectedWinners: SelectedWinnerType[]
  clearSelectedWinners: () => void
  winnerStatusFilter: SweepstakeWinnerStatusType
  sweepstakeName: string
  contestId: number
}

export const SweepstakesWinnerActionsMenu = ({
  selectedWinners,
  clearSelectedWinners,
  winnerStatusFilter,
  sweepstakeName,
  contestId,
}: MenuButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [notifyWinnersModalOpen, setNotifyWinnersModalOpen] = useState(false)
  const setMessage = useSnackbar((state) => state.setMessage)
  const invalidateQueryKeys: QueryKey[] = [
    getSweepstakeDrawingInfoQueryKey(contestId),
  ]

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const bulkUpdateSweepstakeWinnersStatus =
    usePutSweepstakeWinnerBulkUpdateStatus({
      invalidateQueryKeys,
      onSuccess: () => {
        setMessage(`Winners updated successfully.`, 'success')
        clearSelectedWinners()
        handleClose()
      },
      onError: () => {
        setMessage(`Error updating winners`, 'error')
      },
    })

  return (
    <div>
      <Button
        variant="contained"
        onClick={handleClick}
        disabled={
          selectedWinners.length === 0 ||
          (winnerStatusFilter !== 'LocationValidation' &&
            winnerStatusFilter !== 'WinnerValidation' &&
            winnerStatusFilter !== 'AffidavitReceived' &&
            winnerStatusFilter !== 'SentToAP')
        }
      >
        Action To Take
        <KeyboardArrowDown fontSize="small" />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {winnerStatusFilter === 'LocationValidation' && (
          <MenuItem
            onClick={() => {
              bulkUpdateSweepstakeWinnersStatus.mutate({
                sweepstakeWinnerIds: selectedWinners.map(
                  (winner) => winner.sweepstakesWinner.id
                ),
                status: 'WinnerValidation',
              })
            }}
          >
            Validate Winners
          </MenuItem>
        )}
        {winnerStatusFilter === 'WinnerValidation' && (
          <MenuItem
            onClick={() => {
              setNotifyWinnersModalOpen(true)
              handleClose()
            }}
          >
            Notify Winners
          </MenuItem>
        )}
        {winnerStatusFilter === 'AffidavitReceived' && (
          <MenuItem
            onClick={() => {
              bulkUpdateSweepstakeWinnersStatus.mutate({
                sweepstakeWinnerIds: selectedWinners.map(
                  (winner) => winner.sweepstakesWinner.id
                ),
                status: 'SentToAP',
              })
            }}
          >
            Sent to AP
          </MenuItem>
        )}
        {winnerStatusFilter === 'SentToAP' && (
          <MenuItem
            onClick={() => {
              bulkUpdateSweepstakeWinnersStatus.mutate({
                sweepstakeWinnerIds: selectedWinners.map(
                  (winner) => winner.sweepstakesWinner.id
                ),
                status: 'Awarded',
              })
            }}
          >
            Award Winners
          </MenuItem>
        )}
        {winnerStatusFilter === 'Awarded' && (
          <MenuItem onClick={handleClose}>Awarded</MenuItem>
        )}
        {winnerStatusFilter === 'WinnerNotified' && (
          <MenuItem onClick={handleClose}>Notified</MenuItem>
        )}
      </Menu>
      <SweepstakeNotifyWinnersModal
        sweepstakeName={sweepstakeName}
        isModalOpen={notifyWinnersModalOpen}
        toggleIsOpen={() => setNotifyWinnersModalOpen(!notifyWinnersModalOpen)}
        selectedWinners={selectedWinners}
        clearSelectedWinners={clearSelectedWinners}
        isBulkAction={true}
        invalidateQueryKeys={invalidateQueryKeys}
      />
    </div>
  )
}
